import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../contexts/AuthContext'
import { menuService } from '../../services/menuService'
import {
  LayoutDashboard,
  Users,
  Shield,
  Menu as MenuIcon,
  Database,
  Bot,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Home,
  Cog,
  User as UserIcon,
  FileText
} from 'lucide-react'

// Bandera para logs de depuración locales del Sidebar
const DEBUG_LOGS = true

interface SidebarProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  isCollapsed: boolean
  setIsCollapsed: (collapsed: boolean) => void
}

// Mapeo de iconos por nombre
const iconMap: Record<string, any> = {
  Home: Home,
  Dashboard: LayoutDashboard,
  LayoutDashboard: LayoutDashboard,
  Users: Users,
  Shield: Shield,
  Menu: MenuIcon,
  Database: Database,
  Bot: Bot,
  Settings: Settings,
  Cog: Cog,
  User: UserIcon,
  FileText: FileText,
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, setIsOpen, isCollapsed, setIsCollapsed }) => {
  const location = useLocation()
  const { user, logout } = useAuth()

  // Cargar menús dinámicamente desde la base de datos (filtrados por rol de usuario)
  const { data: menuData, isLoading: menusLoading, error: menusError } = useQuery({
    queryKey: ['user-menus-sidebar'],
    queryFn: async () => {
      console.log('Sidebar: Calling getUserMenus...')
      const result = await menuService.getUserMenus()
      console.log('Sidebar: getUserMenus result:', result)
      return result
    },
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
    enabled: true, // SIEMPRE cargar, sin importar si hay usuario
    retry: 3, // Reintentar 3 veces si falla
  })

  // Log de errores
  React.useEffect(() => {
    if (menusError) {
      console.error('Sidebar: Error loading menus:', menusError)
    }
    if (DEBUG_LOGS) {
      console.log('Sidebar: menuData:', menuData)
      console.log('Sidebar: menusLoading:', menusLoading)
      console.log('Sidebar: menusError:', menusError)
    }
  }, [menuData, menusLoading, menusError])

  // Convertir menús de BD a formato de navegación (ya filtrados por rol)
  const navigationItems = React.useMemo(() => {
    if (!menuData) return []

    const convertMenuItem = (menu: any) => {
      const Icon = iconMap[menu.icon] || MenuIcon
      const displayName = menu.name // Mostrar SIEMPRE el nombre del menú
      
      if (DEBUG_LOGS) {
        console.log(`Sidebar: Menu "${menu.name}" icon="${menu.icon}" -> Icon component:`, Icon)
        console.log(`Sidebar: Icon is valid component?`, typeof Icon === 'function')
      }
      
      return {
        id: menu.id,
        name: displayName,
        href: menu.path || '#',
        icon: Icon,
        isActive: menu.isActive !== false, // Usar isActive del backend
        roles: menu.roles,
        children: menu.children?.map(convertMenuItem) || []
      }
    }

    if (DEBUG_LOGS) console.log('Sidebar: Menu data from backend:', menuData)
    
    return (menuData as any[])
      .filter(menu => menu.isActive !== false && menu.isVisible !== false) // Solo menús activos y visibles
      .filter(menu => !menu.parentId) // Solo menús raíz para sidebar
      .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)) // Usar orderIndex del backend
      .map(convertMenuItem)
  }, [menuData])

  if (DEBUG_LOGS) console.log('Sidebar: Navigation items:', navigationItems)

  // Estado de expansión para menús contenedores (sin ruta)
  const [expanded, setExpanded] = React.useState<Set<number>>(new Set())
  const isExpanded = React.useCallback((id: number) => expanded.has(id), [expanded])
  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const isActive = (path: string) => {
    return location.pathname === path
  }

  // Renderizar submenús de forma recursiva (para nietos y más niveles)
  const renderSubMenus = (children: any[]): React.ReactNode => {
    if (!children || children.length === 0) return null
    return (
      <div className="ml-6 mt-1 space-y-1">
        {children.map((child: any) => {
          const hasChildChildren = child.children && child.children.length > 0
          const hasChildPath = child.href && child.href !== '' && child.href !== '#'
          const ChildIcon = child.icon || MenuIcon // child.icon ya es un componente

          const isContainer = hasChildChildren && !hasChildPath

          const childContent = isContainer ? (
            <button
              onClick={() => toggleExpand(child.id)}
              className="group w-full flex items-center justify-between text-sm rounded-md px-3 py-2 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            >
              <div className="flex items-center">
                <ChildIcon className="flex-shrink-0 h-4 w-4 mr-3 text-gray-400 group-hover:text-gray-500" />
                {child.name}
              </div>
              {isExpanded(child.id) ? (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-400" />
              )}
            </button>
          ) : hasChildPath ? (
            <Link
              to={child.href}
              onClick={() => setIsOpen(false)}
              className={`
                group flex items-center text-sm rounded-md px-3 py-2 transition-colors duration-150 ease-in-out
                ${isActive(child.href)
                  ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }
              `}
            >
              <ChildIcon className={`
                flex-shrink-0 h-4 w-4 mr-3
                ${isActive(child.href) ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}
              `} />
              {child.name}
            </Link>
          ) : (
            <div
              className="group flex items-center text-sm rounded-md px-3 py-2 text-gray-600 cursor-default"
            >
              <ChildIcon className="flex-shrink-0 h-4 w-4 mr-3 text-gray-400" />
              {child.name}
            </div>
          )

          return (
            <div key={child.id}>
              {childContent}
              {!isCollapsed && hasChildChildren && isExpanded(child.id) && renderSubMenus(child.children)}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 bg-white shadow-xl transform transition-all duration-300 ease-in-out
        lg:translate-x-0 lg:static lg:inset-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        ${isCollapsed ? 'lg:w-16' : 'lg:w-64'}
        w-64
        print:hidden
        flex flex-col
      `}>
        {/* Header */}
        <div className={`flex items-center h-16 bg-primary-600 ${isCollapsed ? 'justify-center px-4' : 'justify-between px-6'}`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Settings className="h-8 w-8 text-white" />
            </div>
            {!isCollapsed && (
              <div className="ml-3">
                <h1 className="text-lg font-semibold text-white">Admin Panel</h1>
              </div>
            )}
          </div>
          
          {/* Desktop collapse button */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden lg:block p-1 rounded-md text-white hover:bg-primary-700"
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <ChevronLeft className="h-5 w-5" />
            )}
          </button>
          
          {/* Mobile close button */}
          <button
            onClick={() => setIsOpen(false)}
            className="lg:hidden p-1 rounded-md text-white hover:bg-primary-700"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        </div>

        {/* User info */}
        {!isCollapsed && (
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-600">
                    {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'A'}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {user?.firstName ? `${user.firstName} ${user.lastName}` : user?.email}
                </p>
                <p className="text-xs text-gray-500">
                  {user?.roles?.[0]?.name || 'Usuario'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Collapsed user avatar */}
        {isCollapsed && (
          <div className="p-3 border-b border-gray-200 flex justify-center">
            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-xs font-medium text-primary-600">
                {user?.firstName?.charAt(0) || user?.email?.charAt(0) || 'A'}
              </span>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className={`mt-6 ${isCollapsed ? 'px-2' : 'px-3'} flex-1 overflow-y-auto pb-20`}>
          {menusLoading ? (
            <div className="space-y-2">
              {/* Skeleton de carga */}
              {[...Array(5)].map((_, i) => (
                <div key={i} className={`animate-pulse ${isCollapsed ? 'px-2 py-3' : 'px-3 py-2'}`}>
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-gray-300 rounded"></div>
                    {!isCollapsed && <div className="ml-3 w-24 h-4 bg-gray-300 rounded"></div>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              {navigationItems.length > 0 ? (
                navigationItems.map((item) => {
                  const Icon = item.icon
                  const hasChildren = item.children && item.children.length > 0
                  const hasPath = item.href && item.href !== '' && item.href !== '#'
                  const isContainer = hasChildren && !hasPath
                  
                  return (
                    <div key={item.id} className="group relative">
                      {/* Menú padre */}
                      {isContainer ? (
                        <button
                          onClick={() => toggleExpand(item.id)}
                          className={`
                            group w-full flex items-center text-sm font-medium rounded-md transition-colors duration-150 ease-in-out
                            ${isCollapsed ? 'px-2 py-3 justify-center' : 'px-3 py-2'}
                            text-gray-700 hover:bg-gray-100 hover:text-gray-900
                          `}
                          title={isCollapsed ? item.name : ''}
                        >
                          <Icon className={`
                            flex-shrink-0 h-5 w-5
                            ${isCollapsed ? '' : 'mr-3'}
                            text-gray-400 group-hover:text-gray-500
                          `} />
                          {!isCollapsed && (
                            <span className="flex-1">{item.name}</span>
                          )}
                          {!isCollapsed && (
                            isExpanded(item.id) ? (
                              <ChevronDown className="h-4 w-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                            )
                          )}
                        </button>
                      ) : hasPath ? (
                        <Link
                          to={item.href}
                          onClick={() => setIsOpen(false)}
                          className={`
                            group flex items-center text-sm font-medium rounded-md transition-colors duration-150 ease-in-out
                            ${isCollapsed ? 'px-2 py-3 justify-center' : 'px-3 py-2'}
                            ${isActive(item.href)
                              ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-600'
                              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                            }
                          `}
                          title={isCollapsed ? item.name : ''}
                        >
                          <Icon className={`
                            flex-shrink-0 h-5 w-5
                            ${isCollapsed ? '' : 'mr-3'}
                            ${isActive(item.href) ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'}
                          `} />
                          {!isCollapsed && item.name}
                        </Link>
                      ) : (
                        <div className={`
                          group flex items-center text-sm font-medium rounded-md
                          ${isCollapsed ? 'px-2 py-3 justify-center' : 'px-3 py-2'}
                          text-gray-600 cursor-default
                        `}>
                          <Icon className={`
                            flex-shrink-0 h-5 w-5
                            ${isCollapsed ? '' : 'mr-3'}
                            text-gray-400
                          `} />
                          {!isCollapsed && item.name}
                        </div>
                      )}
                      
                      {/* Submenús (hijos y niveles más profundos) */}
                      {hasChildren && !isCollapsed && isExpanded(item.id) && renderSubMenus(item.children)}
                      
                      {/* Tooltip for collapsed state */}
                      {isCollapsed && (
                        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50 whitespace-nowrap top-1/2 transform -translate-y-1/2">
                          {item.name}
                          <div className="absolute left-0 top-1/2 transform -translate-x-1 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                        </div>
                      )}
                    </div>
                  )
                })
              ) : (
                <div className="text-center text-gray-500 text-sm py-4">
                  {!isCollapsed ? 'No hay menús disponibles' : '!'}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Logout button */}
        <div className={`mt-auto ${isCollapsed ? 'p-2' : 'p-3'} border-t border-gray-200 bg-white`}>
          <div className="group relative">
            <button
              onClick={logout}
              className={`
                group flex items-center w-full text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150 ease-in-out
                ${isCollapsed ? 'px-2 py-3 justify-center' : 'px-3 py-2'}
              `}
              title={isCollapsed ? 'Cerrar Sesión' : ''}
            >
              <LogOut className={`
                flex-shrink-0 h-5 w-5 text-gray-400 group-hover:text-gray-500
                ${isCollapsed ? '' : 'mr-3'}
              `} />
              {!isCollapsed && 'Cerrar Sesión'}
            </button>
            
            {/* Tooltip for collapsed state */}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-50 whitespace-nowrap top-1/2 transform -translate-y-1/2">
                Cerrar Sesión
                <div className="absolute left-0 top-1/2 transform -translate-x-1 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default Sidebar
